/**
 * @file    PercentageSettingsManager.tsx
 * @scope   VIEW
 * @summary Advanced-settings panel for the Percentage/TotalCount calc types: multi-select of the
 *          response values that count toward the percentage.
 * @consumes FieldsManager models, percentageSettingsUtils (option/default builders)
 * @produces `PercentageSettings` component + `CalcSettingsPanelProps` (shared panel contract)
 * @sideEffects None — controlled inputs; changes bubble up via `onFieldChange`.
 * @notes WHY it hides flag options: when normalize is ON, flag responses (DK/NA/REF) are excluded
 *        from the selectable options since they are removed from the base.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select, { MultiValue } from 'react-select';
import { PercentageValueOption, SelectedFieldModel } from 'models/Enterprise/FieldsManagerModel';
import {
  buildAvailablePercentageOptions,
  buildDefaultPercentageSettings,
} from '../../utils/views/partitionsManager/calculationsManager/percentageSettingsUtils';

export interface CalcSettingsPanelProps {
  selectedField: SelectedFieldModel;
  onFieldChange?: (field: SelectedFieldModel) => void;
}

const PercentageSettings: React.FC<CalcSettingsPanelProps> = ({ selectedField, onFieldChange }) => {
  const fieldGuid = selectedField.field ? selectedField.field.fieldGuid : undefined;

  // Filter out flag options (DK/NA/REF) if normalize is enabled
  const availableOptions = useMemo(() => {
    const allOptions = buildAvailablePercentageOptions(selectedField.field);

    // If normalize is ON and field has flag responses, exclude flag options from Advanced Settings
    if (selectedField.normalize && selectedField.field?.responses?.some((r: any) => r.flag)) {
      return allOptions.filter(
        (opt) =>
          !opt.path ||
          (opt.path.toLowerCase() !== 'dk' &&
            opt.path.toLowerCase() !== 'na' &&
            opt.path.toLowerCase() !== 'ref')
      );
    }

    return allOptions;
  }, [fieldGuid, selectedField.normalize]);

  const buildDefaults = () => {
    if (selectedField.calculationType?.type === '2') {
      return buildDefaultPercentageSettings(selectedField.field);
    } else {
      return {
        path: selectedField.field?.path ?? 'id',
        selectedValues: [],
      };
    }
  };

  const [selectedValues, setSelectedValues] = useState<PercentageValueOption[]>(
    selectedField.percentageSettings?.selectedValues ?? []
  );
  const [touched, setTouched] = useState(false);

  // Keep refs up-to-date so the unmount cleanup reads the latest values without stale closures
  const selectedValuesRef = useRef(selectedValues);
  const selectedFieldRef = useRef(selectedField);
  const onFieldChangeRef = useRef(onFieldChange);

  useEffect(() => {
    selectedValuesRef.current = selectedValues;
  }, [selectedValues]);

  useEffect(() => {
    selectedFieldRef.current = selectedField;
    onFieldChangeRef.current = onFieldChange;
  });

  // Sync when the parent changes the field (e.g. navigating back and forth)
  useEffect(() => {
    setSelectedValues(selectedField.percentageSettings?.selectedValues ?? []);
    setTouched(false);
  }, [fieldGuid]);

  // On unmount: if the user left without selecting anything, restore defaults
  useEffect(() => {
    return () => {
      if (selectedValuesRef.current.length === 0) {
        const field = selectedFieldRef.current;
        const defaults = buildDefaults();
        onFieldChangeRef.current?.({
          ...field,
          percentageSettings: defaults,
        });
      }
    };
  }, []);

  /** Derive value from the options array so react-select compares identical references. */
  const selectedOptionRefs = useMemo(() => {
    const keys = new Set(selectedValues.map((v) => String(v.value)));
    return availableOptions.filter((o) => keys.has(String(o.value)));
  }, [selectedValues, availableOptions]);

  const handleChange = (options: MultiValue<PercentageValueOption>) => {
    const newValues = [...options];

    setSelectedValues(newValues);

    const baseSettings = selectedField.percentageSettings ?? buildDefaults();

    onFieldChange?.({
      ...selectedField,
      percentageSettings: { ...baseSettings, selectedValues: newValues },
    });
  };

  const showValidation = touched && selectedValues.length === 0;

  return (
    <div className={`diy-input-container${showValidation ? ' field-input--invalid' : ''}`}>
      <div className="diy-material-textfield">
        <Select
          className={`diy-select${showValidation ? ' diy-select--invalid' : ''}`}
          options={availableOptions}
          value={selectedOptionRefs}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          isMulti
          isClearable={false}
          closeMenuOnSelect={false}
          styles={{ control: (s: any) => ({ ...s, maxWidth: '100%' }) }}
          placeholder="Select options..."
        />
        <label className="diy-label select">Values</label>
      </div>
      {showValidation && (
        <div className="diy-required-field-wrapper">
          <span className="diy-required-field">You must select at least one item</span>
        </div>
      )}
    </div>
  );
};

export default PercentageSettings;
